import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
} from '@chakra-ui/react';
import { FileText, ArrowRight } from 'lucide-react';
import {
    fetchCareerAdviceAsync,
    selectCareerAdviceData,
} from '../../candidateDashboard/candidateSlice/CareerAdvice/CareerAdviceSlice';
import {
    fetchSkillDevelopmentAsync,
    selectSkillDevelopmentData,
} from '../../candidateDashboard/candidateSlice/SkillDevelopment/SkillDevelopmentSlice';
import {
    fetchInterviewPreparationAsync,
    selectInterviewPreparation,
} from '../../candidateDashboard/candidateSlice/InterviewPreparation/InterviewPreparationSlice';
import {
    SKILL_DEVELOPMENT_VIEW,
    CAREER_ADVICE_VIEW,
    INTERVIEW_PREPARATION_VIEW,
} from '../../common/URLs';
import { Link } from 'react-router-dom';

const Blogs = () => {
    const dispatch = useDispatch();
    const careerAdviceData = useSelector(selectCareerAdviceData);
    const skillDevelopmentData = useSelector(selectSkillDevelopmentData);
    const interviewPreparationData = useSelector(selectInterviewPreparation);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedBlog, setSelectedBlog] = useState(null);

    useEffect(() => {
        dispatch(fetchCareerAdviceAsync());
        dispatch(fetchSkillDevelopmentAsync());
        dispatch(fetchInterviewPreparationAsync());
    }, [dispatch]);

    const getAttachmentUrlSD = (attachment) => {
        if (!attachment) return null;
        return `${SKILL_DEVELOPMENT_VIEW}/${attachment}`;
    };
    const getAttachmentUrlCA = (attachment) => {
        if (!attachment) return null;
        return `${CAREER_ADVICE_VIEW}/${attachment}`;
    };
    const getAttachmentUrlIP = (attachment) => {
        if (!attachment) return null;
        return `${INTERVIEW_PREPARATION_VIEW}/${attachment}`;
    };

    const limitBlogs = (blogs) => {
        return (blogs || []).slice(0, 4);
    };

    const handleCardClick = (blog) => {
        setSelectedBlog(blog);
        onOpen();
    };

    // IMPORTANT: mainUrl moved to the category level and made absolute (leading slash)
    const blogCategories = [
        {
            title: 'Career Advice',
            mainUrl: '/candidate-dashboard/career-advice',
            blogs: limitBlogs(careerAdviceData || []).map((item) => ({
                id: item.uuid,
                title: item.title,
                description: item.description,
                pdfUrl: getAttachmentUrlCA(item.attachment),
            })),
        },
        {
            title: 'Skill Development',
            mainUrl: '/candidate-dashboard/skill-development',
            blogs: limitBlogs(skillDevelopmentData?.data || []).map((item) => ({
                id: item.uuid,
                title: item.title,
                description: item.description,
                pdfUrl: getAttachmentUrlSD(item.attachment),
            })),
        },
        {
            title: 'Interview Preparation',
            mainUrl: '/candidate-dashboard/interview-preparation',
            blogs: limitBlogs(interviewPreparationData?.data || []).map((item) => ({
                id: item.uuid,
                title: item.title,
                description: item.description,
                pdfUrl: getAttachmentUrlIP(item.attachment),
            })),
        },
    ];

    return (
        <div className='w-full min-h-screen bg-gray-50 p-4 md:p-8'>
            {/* Hero Section */}
            <div className='text-center py-8 md:py-8 bg-[linear-gradient(to_bottom_right,_#1565C0,_#1E88E5,_#26C6DA)] rounded-2xl text-white mb-8'>
                <div className='mx-auto px-6'>
                    <div className='max-w-2xl mx-auto'>
                        <p className='text-sm font-semibold uppercase tracking-wider text-blue-100'>
                            Blogs by IndiaHirings
                        </p>
                        <h1 className='mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl'>
                            Insights for your next move
                        </h1>
                        <p className='mt-3 text-blue-100/90 text-sm'>
                            Explore curated guides to help you stand out, ace interviews, and grow your career.
                        </p>
                    </div>

                    <div className='mt-4 rounded-2xl bg-white/10 p-4 backdrop-blur ring-1 ring-white/20 max-w-md mx-auto'>
                        <ul className='flex justify-center gap-3 text-xs text-blue-50'>
                            <li className='rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/20'>
                                Career Advice
                            </li>
                            <li className='rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/20'>
                                Interview Prep
                            </li>
                            <li className='rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/20'>
                                Skill Development
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Blog Categories */}
            {blogCategories.map((category, index) => (
                <div key={index} className='mb-12'>
                    <div className='flex justify-between items-center border-b-2 border-blue-500 mb-6 pb-2'>
                        <div className='text-2xl md:text-3xl font-bold text-gray-800'>
                            {category.title}
                        </div>

                        <div>
                            <Link
                                to={category.mainUrl} // now defined and absolute
                                className='text-blue-600 hover:text-blue-800 transition duration-300 font-medium'
                            >
                                View All →
                            </Link>
                        </div>
                    </div>

                    {category.blogs.length > 0 ? (
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                            {category.blogs.map((blog) => (
                                <div
                                    key={blog.id}
                                    className='bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer'
                                    onClick={() => handleCardClick(blog)}
                                >
                                    <div className='p-6'>
                                        <h3 className='text-lg font-semibold text-gray-800 mb-3 line-clamp-2'>
                                            {blog.title}
                                        </h3>
                                        <p className='text-gray-600 text-sm mb-4 line-clamp-3'>
                                            {blog.description}
                                        </p>
                                        <div className='flex items-center text-blue-600 text-sm font-medium'>
                                            <span>Read more</span>
                                            <ArrowRight size={16} className='ml-1' />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className='text-center py-8 text-gray-500'>
                            No blogs available in this category
                        </div>
                    )}
                </div>
            ))}

            {/* Modal for Blog Details */}
            <Modal isOpen={isOpen} onClose={onClose} size='xl'>
                <ModalOverlay />
                <ModalContent className='rounded-xl'>
                    <ModalHeader className='text-2xl font-bold text-gray-800 border-b border-gray-200'>
                        {selectedBlog?.title}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody className='py-6'>
                        <p className='text-gray-700 mb-6'>{selectedBlog?.description}</p>
                        {selectedBlog?.pdfUrl && (
                            <a
                                href={selectedBlog.pdfUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300'
                            >
                                <FileText size={18} className='mr-2' />
                                View PDF
                            </a>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>


        </div>
    );
};

export default Blogs;